import { ImageFileType } from "src/shared/components";
import { Nullable } from "src/shared/utils/typescript";
import { ValidationIssue } from "src/shared/utils/validationMessages";

import { ProfileFormValues } from "./types";

/**
 * Character limits mirror the `character_limit` values in the
 * hackerapi `hackthenorth2026-discovery-profiles` pipeline. Keep in sync.
 */
export const NAME_MAX_CHARS = 100;
export const BIO_PROMPT_MAX_CHARS = 100;
export const BIO_MAX_CHARS = 200;
export const PROFILE_PHOTO_CAPTION_MAX_CHARS = 50;
export const SKILL_MAX_CHARS = 50;
export const EXPERIENCE_GOALS_MAX_CHARS = 100;
export const TEAMMATE_CRITERIA_MAX_CHARS = 100;

export const NUM_SKILL_INPUTS = 3;
export const MAX_INTERESTS_COUNT = 5;
export const MAX_TEAM_SIZE = 4;

export const TEAMMATES_NEEDED_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "0, I'm just putting my profile out there!" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

export const WEEKEND_PACE_OPTIONS: {
  value: number;
  label: string;
  tooltip: string;
  chiliCount: number;
  useFire: boolean;
  useBellPepper: boolean;
}[] = [
  {
    value: 0,
    label: "Up for anything",
    tooltip:
      "Down to match whatever energy or pace a group wants to set, chill or intense!",
    chiliCount: 1,
    useFire: false,
    useBellPepper: true,
  },
  {
    value: 1,
    label: "Mild",
    tooltip:
      "Chill pace. Enjoying activities, workshops, and keeping things low stress.",
    chiliCount: 1,
    useFire: false,
    useBellPepper: false,
  },
  {
    value: 2,
    label: "Medium",
    tooltip:
      "Steady rhythm. Moving at a focused pace while leaving room for activities.",
    chiliCount: 2,
    useFire: false,
    useBellPepper: false,
  },
  {
    value: 3,
    label: "Maxxed",
    tooltip: "Locked in. High momentum, no brakes, here to win.",
    chiliCount: 3,
    useFire: true,
    useBellPepper: false,
  },
];

export const HEADSHOT_FILE_TYPES: ImageFileType[] = [
  "png",
  "jpg",
  "jpeg",
  "heic",
  "heif",
];

export const PRONOUN_OPTIONS = ["they/them", "she/her", "he/him", "Other"];
export const OTHER_PRONOUN_OPTION = "Other: ";

export const INTEREST_OPTIONS = [
  "Accessibility Tech",
  "AI / ML",
  "AR / VR",
  "Climate Tech",
  "Creative Tech & Art",
  "Cybersecurity",
  "Dev Tools & Workflow",
  "Education",
  "Embedded Systems",
  "FinTech & Web3",
  "Game Dev",
  "Health & Wellness",
  "Mobility & Cities",
  "Robotics & Hardware",
  "Social & Community",
];

export const getCustomInterestOptions = (interests: string[]): string[] =>
  interests.filter((interest) => !INTEREST_OPTIONS.includes(interest));

export const ICEBREAKER_PROMPTS = [
  "My hacking style is 10% planning and 90%...",
  "My ultimate hacker flex is...",
  "My ideal 3:00 AM hackathon state of mind is...",
  "In a team setting, you can usually rely on me to...",
];

export const hasText = (value: Nullable<string> | undefined): boolean =>
  value != null && value.trim() !== "";

type CoreValidationValues = Pick<
  ProfileFormValues,
  "name" | "pronouns" | "profile_photo" | "bio_prompt" | "bio" | "skills"
>;

/**
 * Mirrors the publish-time validation in hackerapi's
 * `validateDiscoveryProfileArgs`. Drafts can be saved at any point, so these
 * errors only gate the "Publish" action.
 */
export const getCoreFieldsPublishErrors = (values: CoreValidationValues) => {
  const skills = (values.skills ?? []).map((s) => s.trim()).filter(Boolean);
  const hasDuplicateSkills =
    skills.length > 0 && new Set(skills).size !== skills.length;

  return {
    name: !hasText(values.name) ? "Please enter a display name." : undefined,
    profilePhoto: !values.profile_photo?.length
      ? "Please upload a headshot."
      : undefined,
    bioPrompt: !hasText(values.bio_prompt)
      ? "Please select an ice breaker prompt."
      : undefined,
    bio:
      hasText(values.bio_prompt) && !hasText(values.bio)
        ? "Please fill out your ice breaker response."
        : undefined,
    skills: hasDuplicateSkills ? "Your skills must be distinct." : undefined,
  };
};

export const getPublishErrors = (values: ProfileFormValues) => {
  const interests = (values.interests ?? []).filter(Boolean);
  return {
    ...getCoreFieldsPublishErrors(values),
    interests:
      interests.length === 0
        ? "Please select at least one project interest."
        : undefined,
    teammatesNeeded:
      values.teammates_needed == null
        ? "Please select how many teammates you're looking for."
        : undefined,
  };
};

export const getPublishValidationIssues = (
  values: ProfileFormValues
): ValidationIssue[] => {
  const errors = getPublishErrors(values);

  return [
    { label: "Display name", isValid: !errors.name },
    { label: "Headshot", isValid: !errors.profilePhoto },
    { label: "Ice breaker prompt", isValid: !errors.bioPrompt },
    { label: "Ice breaker response", isValid: !errors.bio },
    { label: "Skills", isValid: !errors.skills },
    { label: "Project interests", isValid: !errors.interests },
    { label: "Teammates needed", isValid: !errors.teammatesNeeded },
  ];
};
